import { makeStyles } from "tss-react/mui";
import * as mdi from "@mdi/js";
import { Box, Breadcrumbs, Tooltip, Typography, useTheme } from "@mui/material";
import { MiIcon } from "../../MiIcon/MiIcon";

/**
 * MiFilterBreadcrumbs component renders a breadcrumb navigation with an optional action icon.
 *
 * @param {Object} props - The component props.
 * @param {string} props.rootTitle - The title of the root breadcrumb.
 * @param {function} props.onRootClick - The callback function to be called when the root breadcrumb is clicked.
 * @param {Array<string>} [props.options=[]] - An array of breadcrumb options.
 * @param {function} props.onActionIconClick - The callback function to be called when the action icon is clicked.
 * @param {string} props.actionIconPath - The path of the action icon from the mdi library.
 * @param {string} props.actionIconLabel - The label for the action icon tooltip.
 *
 * @returns {JSX.Element} The rendered MiFilterBreadcrumbs component.
 */
export function MiFilterBreadcrumbs({
  rootTitle,
  onRootClick,
  options = [],
  onActionIconClick,
  actionIconPath,
  actionIconLabel,
}) {
  const { classes } = useStyles();
  const theme = useTheme();

  return (
    <Box className={classes.breadcrumbsContainer}>
      <Breadcrumbs separator="›" className={classes.breadcrumbs}>
        <Typography variant="subtitle2" onClick={onRootClick}>
          {rootTitle}
        </Typography>
        {options.map((option, i) => (
          <Typography
            key={option + i}
            variant="subtitle2"
            className={classes.breadcrumbOption}
          >
            {option}
          </Typography>
        ))}
      </Breadcrumbs>
      <Tooltip
        title={
          <Typography
            variant="subtitle2"
            sx={{ fontSize: theme.fontSize.small }}
          >
            {actionIconLabel}
          </Typography>
        }
        placement="left"
      >
        <Box>
          {onActionIconClick && (
            <MiIcon
              onClick={onActionIconClick}
              path={mdi[actionIconPath]}
              size={0.8}
              color={theme.palette.primary.main}
              style={{ cursor: "pointer" }}
            />
          )}
        </Box>
      </Tooltip>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  breadcrumbsContainer: {
    height: 32,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  breadcrumbs: {
    color: theme.palette.primary.main,
    "& > *": {
      maxHeight: 24,
      minHeight: 24,
      alignItems: "flex-end",
    },
    "& > * > :first-of-type:hover": {
      cursor: "pointer",
      textDecoration: "underline",
    },
  },
  breadcrumbOption: {
    width: 160,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));
